import { mode, StyleFunctionProps, transparentize } from '@chakra-ui/theme-tools';
import { sizes as commonSizes } from './common';

const baseStyle = {
  outline: 'none',
  borderRadius: '3rem',
  lineHeight: 'short',
  _focus: {
    outline: 'none',
    boxShadow: 'none',
  },
};

const sizes = {
  xl: {
    ...commonSizes.xl,
    borderRadius: '82px',
    px: 12,
  },
  lg: {
    ...commonSizes.lg,
    borderRadius: '72px',
    px: 10,
  },
  md: {
    ...commonSizes.md,
    borderRadius: '64px',
    px: 8,
  },
  def: {
    ...commonSizes.def,
    borderRadius: '56px',
    px: 8,
  },
  sm: {
    ...commonSizes.sm,
    borderRadius: '48px',
    px: 6,
  },
  xs: {
    ...commonSizes.xs,
    borderRadius: '40px',
    px: 5,
  },
  '2xs': {
    ...commonSizes['2xs'],
    borderRadius: '32px',
    px: 4,
  },
  '3xs': {
    ...commonSizes['3xs'],
    borderRadius: '28px',
    px: 3,
  },
  '4xs': {
    ...commonSizes['4xs'],
    borderRadius: '24px',
    px: 2,
  },
  '5xs': {
    ...commonSizes['5xs'],
    borderRadius: '20px',
    px: 2,
  },
};

const borderSize: Dict = {
  xl: '3px',
  lg: '2px',
  md: '2px',
  xs: '1px',
  '2xs': '1px',
  '3xs': '1px',
  '4xs': '1px',
  '5xs': '1px',
};
const boxShadow: Dict = {
  sm: 'button-sm',
  def: 'button-sm',
  md: 'button-sm',
  lg: 'button',
  xl: 'button',
};

const variantColors: Dict<[number, number]> = {
  50: [50, 1000],
  100: [100, 900],
  200: [200, 800],
  300: [300, 700],
  400: [400, 600],
  500: [500, 500],
  600: [600, 400],
  700: [700, 300],
  800: [800, 200],
  900: [900, 100],
  1000: [1000, 50],
};

const textColor = (base: number) => (base > 600 ? 'white' : 'gray.900');
const validColor = (base: number) => (base > 1100 ? 1100 : base < 0 ? 0 : base);
const forcedMode = (isForced: boolean) => (light: string, dark: string) =>
  mode(light, isForced ? light : dark);
const commonOutline = (size: keyof typeof borderSize = 'md') => ({
  bg: 'transparent',
  border: `${borderSize[size] || '2px'} solid`,
});

function outline(props: StyleFunctionProps) {
  const { colorScheme, size: s, bg = 'transparent' } = props;
  const isForced = colorScheme.startsWith('!');
  const c = isForced ? colorScheme.slice(1) : colorScheme;
  const cMode = forcedMode(isForced);
  if (c === 'white') {
    return {
      ...commonOutline(s),
      borderColor: cMode(`white`, `black`)(props),
      color: cMode(`white`, `black`)(props),
      _hover: {
        bg,
        color: cMode(`gray.100`, `gray.1000`)(props),
        borderColor: cMode(`gray.100`, `gray.1000`)(props),
      },
      _active: {
        bg,
        color: cMode(`gray.200`, `gray.900`)(props),
        borderColor: cMode(`gray.200`, `gray.900`)(props),
      },
    };
  }
  if (c === 'black') {
    return {
      ...commonOutline(s),
      borderColor: cMode(`black`, `white`)(props),
      color: cMode(`black`, `white`)(props),
      _hover: {
        bg,
        color: cMode(`gray.1000`, `gray.100`)(props),
        borderColor: cMode(`gray.1000`, `gray.100`)(props),
      },
      _active: {
        bg,
        color: cMode(`gray.900`, `gray.200`)(props),
        borderColor: cMode(`gray.900`, `gray.200`)(props),
      },
    };
  }
  const [color = c, value] = (c || '').split('.');
  const [light, dark] = variantColors[value] || [700, 800];
  const lightCalc = light === 50 ? 100 : light;
  const darkCalc = dark === 50 ? 100 : dark;
  const borderColor = cMode(`${color}.${light}`, `${color}.${dark}`)(props);
  const borderColorH = cMode(
    `${color}.${validColor(lightCalc - 100)}`,
    `${color}.${validColor(darkCalc - 100)}`,
  )(props);
  const borderColorA = cMode(
    `${color}.${validColor(lightCalc + 100)}`,
    `${color}.${validColor(darkCalc + 100)}`,
  )(props);
  return {
    ...commonOutline(s),
    borderColor,
    color: borderColor,
    _hover: {
      bg,
      borderColor: borderColorH,
      color: borderColorH,
    },
    _active: {
      bg,
      borderColor: borderColorA,
      color: borderColorA,
    },
  };
}

function solid(props: StyleFunctionProps) {
  const { colorScheme, size: s } = props;
  const isForced = colorScheme.startsWith('!');
  const c = isForced ? colorScheme.slice(1) : colorScheme;
  const cMode = forcedMode(isForced);
  const bs = boxShadow[s] || 'button-xs';
  if (c === 'white') {
    const bg = cMode(`white`, `gray.900`)(props);
    return {
      boxShadow: bs,
      color: cMode(`gray.900`, `white`)(props),
      bg,
      _hover: {
        bg: cMode(`gray.100`, `gray.800`)(props),
        _disabled: {
          bg,
        },
      },
      _active: { bg: cMode(`gray.200`, `gray.1000`)(props) },
    };
  }
  if (c === 'black') {
    const bg = cMode(`black`, `white`)(props);
    return {
      boxShadow: bs,
      color: cMode(`white`, `black`)(props),
      bg,
      _hover: {
        bg: cMode(`gray.1000`, `gray.100`)(props),
        _disabled: {
          bg,
        },
      },
      _active: { bg: cMode(`gray.1100`, `gray.200`)(props) },
    };
  }

  const [color = c, value] = (c || '').split('.');
  const [light, dark] = variantColors[value] || [800, 800];
  const lightCalc = light === 50 ? 100 : light;
  const darkCalc = dark === 50 ? 100 : dark;
  const bg = cMode(`${color}.${light}`, `${color}.${dark}`)(props);
  return {
    bg,
    color: cMode(textColor(light), textColor(dark))(props),
    boxShadow: bs,
    _hover: {
      bg: cMode(
        `${color}.${validColor(lightCalc + 100)}`,
        `${color}.${validColor(darkCalc - 100)}`,
      )(props),
      _disabled: {
        bg,
      },
    },
    _active: {
      bg: cMode(
        `${color}.${validColor(lightCalc + 200)}`,
        `${color}.${validColor(darkCalc + 100)}`,
      )(props),
    },
  };
}

const variants = {
  simple: ({ bg, backgroundColor }: StyleFunctionProps) => ({
    _hover: { bg: bg || backgroundColor, opacity: 0.8 },
  }),
  minimal: (): Dict => ({
    p: 0,
    h: 'auto',
    minW: 0,
    fontWeight: 'normal',
    fontSize: 'inherit',
    verticalAlign: 'baseline',
    _hover: { opacity: 0.8 },
  }),
  outline,
  solid,
  'solid-light': (props: StyleFunctionProps) => {
    const { colorScheme: c, theme } = props;
    return {
      bg: mode(`${c}.200`, transparentize(`${c}.800`, 0.15)(theme as Dict))(props),
      color: `${c}.800`,
      _hover: {
        bg: mode(`${c}.300`, transparentize(`${c}.800`, 0.175)(theme as Dict))(props),
      },
      _active: {
        bg: mode(`${c}.300`, transparentize(`${c}.800`, 0.2)(theme as Dict))(props),
      },
    };
  },
};

const defaultProps = {
  variant: 'solid',
  size: 'md',
  colorScheme: 'gray',
};

export default {
  baseStyle,
  sizes,
  variants,
  defaultProps,
};
